/**
 * Frontend type definitions aligned with backend API types
 * Re-exports and extends backend types for frontend use
 */

// Import taxonomy types at top of file
import type { TaxonomyExtraction, TaxonomySchema } from './taxonomy';
import type { TemplateSnapshot } from './template';

// ============================================================================
// TEAM TYPES
// ============================================================================

export type TeamRole = 'admin' | 'member';

export interface Team {
  teamId: string;
  name: string;
  memberCount: number;
  role: TeamRole;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// PROJECT TYPES
// ============================================================================

export type ProjectStatus = 'active' | 'archived' | 'deleted';

export interface BrandContext {
  logoUrl?: string;
  primaryColors?: string[];
  distinctiveAssets?: string[];
  productNames?: string[];
}

export interface Project {
  projectId: string;
  teamId: string;
  name: string;
  brandName?: string;
  description?: string;
  brandContext?: BrandContext;
  taxonomySchema?: TaxonomySchema;
  templateId?: string;
  /** @deprecated Unified templates use only templateId. Kept for legacy data. */
  imageTemplateId?: string;
  status: ProjectStatus;
  creativeCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface ProjectStats {
  totalCreatives: number;
  analyzedCreatives: number;
  notAnalyzedCreatives: number;
  scaleCount: number;
  fixCount: number;
  killCount: number;
}

// ============================================================================
// CREATIVE TYPES
// ============================================================================

export type CreativeStatus = 'uploading' | 'uploaded' | 'processing' | 'ready' | 'error' | 'deleted';
export type CampaignObjective = 'Awareness' | 'Consideration' | 'Performance';
/** @deprecated Use Placement instead */
export type AdPlacement = 'Feed' | 'Reels' | 'Stories' | 'InStream' | 'Search' | 'Other';
export type AudienceType = 'Prospecting' | 'Retargeting';

// Unified placement type - platform agnostic (Phase 17)
export type Placement = 'Feed' | 'Reels' | 'Stories' | 'InStream' | 'Search' | 'Other';

export interface PlacementData {
  placement: Placement;
  impressions?: number;
}
/** @deprecated Use MediaFormat instead */
export type VideoFormat = 'mp4' | 'mov' | 'webm';
export type MediaFormat = 'mp4' | 'mov' | 'webm' | 'jpg' | 'png' | 'webp';
export type MediaType = 'video' | 'image';

export interface CreativeMetadata {
  objective?: CampaignObjective;
  placement?: AdPlacement;
  audience?: string;
  product?: string;
  isPromo?: boolean;
}

/**
 * Performance metrics from Meta Ads API.
 *
 * Field names use camelCase to match API response convention.
 * All fields optional since not every ad will have all metrics.
 * Uses index signature to allow any metric name.
 */
export interface PerformanceMetrics {
  // Delivery metrics
  spend?: number;
  impressions?: number;
  reach?: number;
  frequency?: number;
  cpm?: number;
  // Traffic metrics
  clicks?: number;
  linkClicks?: number;
  outboundClicks?: number;
  ctr?: number;
  linkCtr?: number;
  outboundCtr?: number;
  cpc?: number;
  costPerLinkClick?: number;
  landingPageViews?: number;
  costPerLandingPageView?: number;
  // Engagement metrics
  postEngagements?: number;
  reactions?: number;
  comments?: number;
  shares?: number;
  costPerEngagement?: number;
  // Video metrics
  videoViews?: number;
  thruplays?: number;
  costPerThruplay?: number;
  video3sViews?: number;
  video10sViews?: number;
  video25Percent?: number;
  video50Percent?: number;
  video75Percent?: number;
  video95Percent?: number;
  avgWatchTime?: number;
  // Conversion metrics
  viewContent?: number;
  addToCart?: number;
  initiateCheckout?: number;
  purchases?: number;
  purchaseValue?: number;
  // Allow additional metrics
  [key: string]: number | undefined;
}

export interface Creative {
  creativeId: string;
  projectId: string;
  teamId: string;
  name: string;
  fileSize: number;
  duration: number;
  format: string;
  mediaType?: MediaType; // Optional for backward compat; defaults to 'video' if missing
  downloadUrl?: string;
  thumbnailUrl?: string;
  metadata?: CreativeMetadata;
  performanceMetrics?: PerformanceMetrics;
  latestAnalysisId?: string;
  latestDecision?: AnalysisDecision;
  taxonomyExtraction?: TaxonomyExtraction | null;
  analysisCount: number;
  status: CreativeStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  // Meta Ads integration fields
  metaAdId?: string;
  metaAdSetId?: string;
  metaCampaignId?: string;
  metaConnectionId?: string;
  metaPlacement?: string;
  metricsLastSynced?: string; // ISO timestamp when Meta metrics were last synced
  metaOptimizationGoal?: string; // Meta campaign optimization goal (e.g., THRUPLAY, LINK_CLICKS)
  metaThumbnailUrl?: string; // Meta ad thumbnail URL
  // Unified placements field (Phase 17)
  placements?: PlacementData[];
}

// ============================================================================
// ANALYSIS TYPES
// ============================================================================

export type AnalysisStatus = 'queued' | 'processing' | 'complete' | 'failed';
export type AnalysisDecision = 'Scale' | 'Fix' | 'Kill';
export type Confidence = 'High' | 'Medium' | 'Low';
export type BrandVisibility = 'none' | 'visible' | 'partial' | 'clear';
export type CutType = 'realSceneChange' | 'sameSceneTextChange' | 'cameraMove' | 'jumpCut';
export type EffortLevel = 'S' | 'M' | 'L';

export interface TimelineEntry {
  timeStart: string; // MM:SS
  timeEnd: string; // MM:SS
  scene: string;
  text: string;
  audio: string;
  brandVisibility: BrandVisibility;
  productVisibility: BrandVisibility;
  cutType: CutType;
}

export interface DistinctiveAsset {
  asset: string;
  type: 'logo' | 'pack' | 'color' | 'character' | 'sonic' | 'tagline';
  timestamp: string; // MM:SS
  duration: string; // e.g., "2.5s"
  clarity: 'clear' | 'partial';
}

export interface BrandMetrics {
  logoDurationSeconds: number;
  productDurationSeconds: number;
  brandCueBy2s: boolean;
  timeToFirstBrandCue: string | null; // MM:SS or null
  brandVisibleAtSeconds?: boolean[];
  productVisibleAtSeconds?: boolean[];
}

export interface QualityCheck {
  status: 'PASS' | 'FAIL';
  evidence: string;
}

export interface QualityGate {
  productionErrors: QualityCheck;
  formatHygiene: QualityCheck;
  brandTiming: QualityCheck;
  clearClaim: QualityCheck;
  productShown: QualityCheck;
  ctaPresent: QualityCheck;
  brandColorsUsed: QualityCheck;
}

/** Dynamic quality gate - keys are gate IDs from template */
export interface DynamicQualityGate {
  [gateId: string]: QualityCheck;
}

export interface CategoryEntryPoint {
  name: string;
  trigger: string;
  takeaway: string;
  strength?: number;
  penetration?: 'high' | 'medium' | 'niche';
}

export type EmotionalRegister = 'brand_building' | 'hybrid' | 'activation';
export type DistinctivenessLevel = 'generic' | 'partial' | 'distinctive' | 'iconic';

export interface DiagnosisSection {
  facts: string[];
  interpretation: string;
  emotionalRegister?: EmotionalRegister | null;
}

export interface BrandLinkageSection {
  level: 'Low' | 'Medium' | 'High';
  facts: string[];
  interpretation: string;
  distinctiveness?: DistinctivenessLevel | null;
  distinctivenessRationale?: string | null;
}

export interface Diagnosis {
  cepMessage: DiagnosisSection;
  fluency: DiagnosisSection;
  emotion: DiagnosisSection;
  brandLinkage: BrandLinkageSection;
  structure: DiagnosisSection;
}

/** Single dynamic diagnosis dimension result (from template dimensions) */
export interface DynamicDiagnosisDimension {
  facts: string[];
  interpretation: string;
  value: boolean | number | string | string[];
}

/** Dynamic diagnosis - keys are dimension IDs from template */
export interface DynamicDiagnosis {
  [dimensionId: string]: DynamicDiagnosisDimension;
}

export interface PerformanceHypothesis {
  hypothesis: string;
  change: string;
  metric: string;
  expectedImpact: string;
  mechanism: string;
  risk: string;
}

export interface Decision {
  call: AnalysisDecision;
  confidence: Confidence;
  primaryReason: string;
  secondaryReasons: string[];
  ifWeDoNothing: string;
}

export interface EditInstruction {
  rank: number;
  timestamp: string; // MM:SS or range
  instruction: string;
  principle: string;
  effort: EffortLevel;
}

export interface AnalysisContext {
  brand: string;
  objective: CampaignObjective;
  placement: AdPlacement;
}

export interface AnalysisResult {
  context: AnalysisContext;
  timeline: TimelineEntry[];
  distinctiveAssets: DistinctiveAsset[];
  brandMetrics: BrandMetrics;
  qualityGate: QualityGate | DynamicQualityGate;
  cep: CategoryEntryPoint[];
  diagnosis: Diagnosis | DynamicDiagnosis;
  hypotheses: PerformanceHypothesis[];
  decision: Decision;
  edits: EditInstruction[];
  executiveSummary: string;
}

// ============================================================================
// IMAGE ANALYSIS TYPES
// ============================================================================

export interface CompositionAnalysis {
  focalPoint: string;
  visualFlow: string;
  balance: 'balanced' | 'weighted-left' | 'weighted-right' | 'top-heavy' | 'bottom-heavy';
  whitespaceUsage: 'adequate' | 'cramped' | 'excessive';
}

export interface TextElementAnalysis {
  content: string;
  role: 'headline' | 'subhead' | 'body' | 'cta' | 'legal' | 'offer';
  location: string;
  readability: 'clear' | 'marginal' | 'illegible';
  estimatedTextAreaPercent: number;
}

export interface ColorAnalysis {
  dominantColors: string[];
  brandColorMatch: boolean;
  contrast: 'high' | 'medium' | 'low';
}

export interface VisualAnalysis {
  composition: CompositionAnalysis;
  textElements: TextElementAnalysis[];
  colorAnalysis: ColorAnalysis;
}

export interface ImageBrandMetrics {
  logoPresent: boolean;
  logoProminence: 'dominant' | 'visible' | 'subtle' | 'absent';
  logoLocation: string;
  productPresent: boolean;
  productProminence: 'dominant' | 'visible' | 'subtle' | 'absent';
  brandIdentifiableAtGlance: boolean;
  distinctiveAssetsPresent: string[];
}

export interface ImageDistinctiveAsset {
  asset: string;
  type: 'logo' | 'pack' | 'color' | 'character' | 'sonic' | 'tagline';
  location: string;
  prominence: 'dominant' | 'visible' | 'subtle';
  clarity: 'clear' | 'partial';
}

export interface ImageDiagnosis {
  cepMessage: DiagnosisSection;
  visualHierarchy: DiagnosisSection;
  emotion: DiagnosisSection;
  brandLinkage: BrandLinkageSection;
  composition: DiagnosisSection;
}

export interface ImageEditInstruction {
  rank: number;
  location: string;
  instruction: string;
  principle: string;
  effort: EffortLevel;
}

export interface ImageAnalysisResult {
  context: AnalysisContext;
  visualAnalysis: VisualAnalysis;
  brandMetrics: ImageBrandMetrics;
  distinctiveAssets: ImageDistinctiveAsset[];
  qualityGate: QualityGate | DynamicQualityGate;
  cep: CategoryEntryPoint[];
  diagnosis: ImageDiagnosis | DynamicDiagnosis;
  hypotheses: PerformanceHypothesis[];
  decision: Decision;
  edits: ImageEditInstruction[];
  executiveSummary: string;
}

/** Type guard to distinguish ImageAnalysisResult from video AnalysisResult */
export function isImageAnalysisResult(result: AnalysisResult | ImageAnalysisResult): result is ImageAnalysisResult {
  return 'visualAnalysis' in result;
}

export interface Analysis {
  analysisId: string;
  creativeId: string;
  projectId: string;
  teamId: string;
  version: number;
  status: AnalysisStatus;
  result?: AnalysisResult | ImageAnalysisResult;
  taxonomyExtraction?: TaxonomyExtraction;
  templateId?: string;
  templateSnapshot?: TemplateSnapshot;
  errorMessage?: string;
  requestedBy: string;
  requestedAt: string;
  completedAt?: string;
  processingTimeMs?: number;
}

// ============================================================================
// API REQUEST TYPES
// ============================================================================

export interface CreateTeamRequest {
  name: string;
}

export interface UpdateTeamRequest {
  name?: string;
}

// ============================================================================
// TEAM INVITE TYPES
// ============================================================================

export type InviteStatus = 'pending' | 'accepted' | 'expired';

export interface TeamMember {
  userId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string;  // Deprecated
  role: TeamRole;
  joinedAt: string;
}

export interface TeamInvite {
  email: string;
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  daysAgo: number;
  resendCount: number;
}

export interface PendingInvitesResponse {
  invites: TeamInvite[];
  count: number;
  limit: number;
}

export interface InvitePreview {
  valid?: boolean;
  expired?: boolean;
  alreadyAccepted?: boolean;
  email: string;
  teamId?: string;
  teamName?: string;
  inviterName?: string;
  expiresAt?: string;
  message?: string;
}

export interface AcceptInviteResponse {
  success?: boolean;
  requiresAuth?: boolean;
  alreadyMember?: boolean;
  teamId: string;
  teamName?: string;
  message: string;
}

export interface CreateInviteRequest {
  email: string;
}

export interface CreateInviteResponse extends TeamInvite {
  emailSent: boolean;
}

export interface CreateProjectRequest {
  name: string;
  brandName?: string;
  description?: string;
  brandContext?: BrandContext;
}

export interface UpdateProjectRequest {
  name?: string;
  brandName?: string;
  description?: string;
  brandContext?: BrandContext;
  status?: ProjectStatus;
  templateId?: string | null;
  /** @deprecated Unified templates use only templateId */
  imageTemplateId?: string | null;
}

export interface UploadUrlRequest {
  fileName: string;
  fileSize: number;
  contentType: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  creativeId: string;
  s3Key: string;
  thumbnailUploadUrl: string;
  thumbnailS3Key: string;
  expiresIn: number;
}

export interface CreateCreativeRequest {
  creativeId: string;
  projectId: string;
  name: string;
  s3Key: string;
  thumbnailS3Key?: string;
  fileSize: number;
  duration?: number; // Optional for images (no duration)
  format: string;
  mediaType?: MediaType;
  metadata?: CreativeMetadata;
  performanceMetrics?: PerformanceMetrics;
  placements?: { placement: string }[];
}

export interface UpdateCreativeRequest {
  name?: string;
  metadata?: CreativeMetadata;
  performanceMetrics?: PerformanceMetrics;
}

// ============================================================================
// LIST & FILTER TYPES
// ============================================================================

export interface PaginationParams {
  limit?: number;
  cursor?: string;
}

export interface CreativeFilterParams extends PaginationParams {
  projectId?: string;
  status?: CreativeStatus;
  decision?: AnalysisDecision;
  objective?: CampaignObjective;
  placement?: AdPlacement;
  startDate?: string;
  endDate?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'decision';
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// USER PROFILE TYPES
// ============================================================================

export interface UserProfile {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  name?: string | null;  // Deprecated, for backward compatibility
  timezone: string;
  profileComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  name?: string;  // Deprecated
  timezone?: string;
}

// ============================================================================
// RE-EXPORT TAXONOMY TYPES
// ============================================================================

export type {
  TaxonomyOption,
  TaxonomyField,
  TaxonomySchema,
  TaxonomyExtractionStatus,
  TaxonomyValueSource,
  TaxonomyValue,
  TaxonomyExtraction,
  SaveTaxonomySchemaRequest,
  TaxonomySchemaResponse,
} from './taxonomy';

export type {
  MetaConnectionStatus,
  MetaConnection,
  MetaAuthorizeResponse,
  MetaConnectionResponse,
} from './meta';

export type {
  Gate,
  GateRole,
  VerdictRules,
  Template,
  StarterTemplateInfo,
  TemplateListResponse,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ForkTemplateRequest,
  DeleteTemplateResponse,
  TemplateSnapshot,
  CustomKpiStatus,
  CustomKpiDefinition,
  CreateCustomKpiRequest,
  UpdateCustomKpiRequest,
  KpiOption,
} from './template';
